// src/crm/hooks/useCampaigns.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { Campaign } from '../../lib/types'

export function useCampaigns(appId: string | null) {
  return useQuery<Campaign[]>({
    queryKey: ['campaigns', appId],
    queryFn: () =>
      api
        .get<{ campaigns: Campaign[] }>(`/api/campaigns?appId=${appId ?? ''}`)
        .then((r) => r.campaigns),
    enabled: appId !== null,
  })
}

export function useCampaign(id: string) {
  return useQuery<Campaign>({
    queryKey: ['campaign', id],
    queryFn: () =>
      api.get<{ campaign: Campaign }>(`/api/campaigns/${id}`).then((r) => r.campaign),
  })
}

export type CampaignInput = {
  name: string
  goal: string | null
  budgetAmount: number | null
  currency: string
  status: 'planning' | 'active' | 'done'
  startDate: string | null
  endDate: string | null
}

export function useCreateCampaign(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CampaignInput) =>
      api
        .post<{ campaign: Campaign }>('/api/campaigns', { ...data, appId })
        .then((r) => r.campaign),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campaigns', appId] })
    },
  })
}

export function useUpdateCampaign(id: string, appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CampaignInput) =>
      api
        .put<{ campaign: Campaign }>(`/api/campaigns/${id}`, data)
        .then((r) => r.campaign),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campaigns', appId] })
      void qc.invalidateQueries({ queryKey: ['campaign', id] })
    },
  })
}

export function useDeleteCampaign(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean }>(`/api/campaigns/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campaigns', appId] })
    },
  })
}
